import React, { useMemo, useState } from 'react'
import { Table, Button, Badge, ButtonGroup, Alert, Input, FormGroup, Label } from 'reactstrap'
import { useQuery, useMutation } from '@apollo/client'
import { GET_ADMIN_INBOX_MEMBERS, GET_ADMIN_INBOXES } from './Queries'
import { REMOVE_INBOX_MEMBER } from './Mutations'
import AddEditInboxMemberModal from './AddEditInboxMemberModal'

const ROLE_COLORS = {
    'admin': 'danger',
    'member': 'primary'
}

const ROLE_LABELS = {
    'admin': 'Admin',
    'member': 'Member'
}

export default function InboxMemberManagement() {
    const [showModal, setShowModal] = useState(false)
    const [selectedMember, setSelectedMember] = useState(null)
    const [filterInboxId, setFilterInboxId] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [feedback, setFeedback] = useState(null)

    const { data: inboxesData } = useQuery(GET_ADMIN_INBOXES, {
        variables: { isArchived: false }
    })

    const { data, loading, error, refetch } = useQuery(GET_ADMIN_INBOX_MEMBERS, {
        variables: { 
            inbox: filterInboxId || undefined
        },
        fetchPolicy: 'network-only'
    })

    const [removeInboxMember] = useMutation(REMOVE_INBOX_MEMBER)

    const members = data?.allInboxMembers?.edges?.map(edge => edge.node) || []
    const inboxes = inboxesData?.myAdminInboxes?.edges?.map(edge => edge.node) || []

    const filteredMembers = useMemo(() => {
        const byInbox = filterInboxId
            ? members.filter(member => member.inbox?.id === filterInboxId)
            : members

        if (!searchTerm.trim()) {
            return byInbox
        }

        const lower = searchTerm.trim().toLowerCase()
        return byInbox.filter(member => {
            const username = member.user?.username?.toLowerCase() || ''
            const email = member.user?.email?.toLowerCase() || ''
            const fullName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.toLowerCase()
            return username.includes(lower) || email.includes(lower) || fullName.includes(lower)
        })
    }, [filterInboxId, members, searchTerm])

    const closeModal = () => {
        setShowModal(false)
        setSelectedMember(null)
    }

    const handleAdd = () => {
        setSelectedMember(null)
        setShowModal(true)
    }

    const handleEdit = (member) => {
        setSelectedMember(member)
        setShowModal(true)
    }

    const handleRemove = (member) => {
        const userName = member.user?.username || 'this user'
        const inboxName = member.inbox?.name || 'the inbox'
        if (window.confirm(`Are you sure you want to remove ${userName} from ${inboxName}?`)) {
            removeInboxMember({
                variables: {
                    id: member.id
                }
            })
                .then(({ data: mutationData }) => {
                    const response = mutationData?.messagingRemoveInboxMember
                    if (response?.ok) {
                        refetch()
                        setFeedback({
                            type: 'success',
                            message: `${userName} has been removed from ${inboxName}.`
                        })
                    } else {
                        setFeedback({
                            type: 'danger',
                            message: response?.errors?.join(', ') || 'Failed to remove member.'
                        })
                    }
                })
                .catch((mutationError) => {
                    setFeedback({
                        type: 'danger',
                        message: mutationError.message || 'Unexpected error removing member.'
                    })
                })
        }
    }

    const handleModalSuccess = (message) => {
        refetch()
        if (message) {
            setFeedback({
                type: 'success',
                message
            })
        }
    }

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        )
    }

    if (error) {
        return <Alert color="danger">Error loading inbox members: {error.message}</Alert>
    }

    const showEmptyState = filteredMembers.length === 0

    return (
        <div className="comms-admin-section">
            <div className="comms-admin-section-header">
                <div>
                    <h3>Inbox Members</h3>
                    <p className="text-muted">Manage team members and their permissions</p>
                </div>
                <div>
                    <Button color="primary" onClick={handleAdd}>
                        <i className="fa fa-plus"></i> Add Member
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert color={feedback.type}>
                    <i className="fa fa-close" onClick={() => setFeedback(null)}></i> {feedback.message}
                </Alert>
            )}

            <div className="comms-admin-filters mb-3">
                <FormGroup className="mb-0">
                    <Label for="inboxFilter" className="mr-2">Filter by Inbox:</Label>
                    <Input
                        type="select"
                        id="inboxFilter"
                        value={filterInboxId}
                        onChange={(e) => setFilterInboxId(e.target.value)}
                        style={{ width: '250px', display: 'inline-block' }}
                    >
                        <option value="">All Inboxes</option>
                        {inboxes.map((inbox) => (
                            <option key={inbox.id} value={inbox.id}>
                                {inbox.name}
                            </option>
                        ))}
                    </Input>
                </FormGroup>
                <FormGroup className="mb-0 flex-grow-1">
                    <Input
                        type="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search members by name or email…"
                    />
                </FormGroup>
            </div>

            {showEmptyState ? (
                <Alert color="info">
                    {searchTerm
                        ? 'No members match your search.'
                        : 'No members found. Add members to your inboxes to get started.'}
                </Alert>
            ) : (
                <Table responsive striped hover className="comms-admin-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Inbox</th>
                            <th>Role</th>
                            <th>Can Be Assigned</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredMembers.map((member) => (
                            <tr key={member.id}>
                                <td>
                                    <strong>{member.user?.username || 'N/A'}</strong>
                                    {member.user?.firstName && member.user?.lastName && (
                                        <div className="text-muted small">
                                            {member.user.firstName} {member.user.lastName}
                                        </div>
                                    )}
                                </td>
                                <td>{member.user?.email || 'N/A'}</td>
                                <td>{member.inbox?.name || 'N/A'}</td>
                                <td>
                                    <Badge color={ROLE_COLORS[member.role] || 'secondary'}>
                                        {ROLE_LABELS[member.role] || member.role}
                                    </Badge>
                                </td>
                                <td>
                                    {member.canBeAssigned ? (
                                        <Badge color="success">Yes</Badge>
                                    ) : (
                                        <Badge color="secondary">No</Badge>
                                    )}
                                </td>
                                <td>
                                    <ButtonGroup size="sm">
                                        <Button
                                            color="info"
                                            outline
                                            onClick={() => handleEdit(member)}
                                            title="Edit Role"
                                        >
                                            <i className="fa fa-edit"></i>
                                        </Button>
                                        <Button
                                            color="danger"
                                            outline
                                            onClick={() => handleRemove(member)}
                                            title="Remove"
                                        >
                                            <i className="fa fa-trash"></i>
                                        </Button>
                                    </ButtonGroup>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <AddEditInboxMemberModal
                isOpen={showModal}
                toggle={closeModal}
                inboxMember={selectedMember}
                onSuccess={handleModalSuccess}
            />
        </div>
    )
}
